import React, { useState, useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  useLLDPractice,
  RequirementItem,
  RequirementCategory,
} from '../../context/LLDPracticeContext';

// ─── Category metadata ───────────────────────────────────────────────────────────

const CATEGORIES: {
  id: RequirementCategory;
  label: string;
  hint: string;
  empty: string;
  dot: string;
}[] = [
    { id: 'functional', label: 'Functional', hint: 'What the system must do', empty: 'What must the system do? e.g. "Assign the nearest free spot to a vehicle."', dot: 'bg-primary-500' },
    { id: 'non-functional', label: 'Non-functional', hint: 'Performance, scale, security, extensibility', empty: 'Qualities & constraints — performance, scale, concurrency, security.', dot: 'bg-amber-500' },
    { id: 'assumption', label: 'Assumptions', hint: 'What you assume and state up front', empty: 'What are you taking as given? e.g. "Payment is handled by an external service."', dot: 'bg-gray-400' },
  ];

const MAX_REQUIREMENTS = 40;

// ─── Component ──────────────────────────────────────────────────────────────────

const RequirementsPanel: React.FC = () => {
  const { state, dispatch } = useLLDPractice();
  const requirements = state.workspaceState.requirements ?? [];

  const [text, setText] = useState('');
  const [category, setCategory] = useState<RequirementCategory>('functional');
  const [error, setError] = useState<string | null>(null);

  const handleAdd = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed) {
      setError('Write a requirement first');
      return;
    }
    if (requirements.length >= MAX_REQUIREMENTS) {
      setError(`Maximum of ${MAX_REQUIREMENTS} requirements reached`);
      return;
    }
    const item: RequirementItem = {
      id: crypto.randomUUID(),
      text: trimmed,
      category,
    };
    dispatch({ type: 'ADD_REQUIREMENT', payload: item });
    setText('');
    setError(null);
  }, [text, category, requirements.length, dispatch]);

  const handleRemove = useCallback(
    (id: string) => {
      dispatch({ type: 'REMOVE_REQUIREMENT', payload: id });
    },
    [dispatch]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && text.trim()) handleAdd();
  };

  const grouped = (cat: RequirementCategory) =>
    requirements.filter((r) => r.category === cat);

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Add Requirement Form */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={handleKeyDown}
            placeholder="e.g. Track real-time availability per level"
            maxLength={160}
            className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
          />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as RequirementCategory)}
            className="sm:w-44 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
          >
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
          <button
            onClick={handleAdd}
            disabled={!text.trim() || requirements.length >= MAX_REQUIREMENTS}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 transition-colors flex-shrink-0"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-500">
          {CATEGORIES.find((c) => c.id === category)?.hint}
        </p>
        {error && <p className="mt-1 text-sm text-rose-500 dark:text-rose-400">{error}</p>}
      </div>

      {/* Requirements list — split by category */}
      <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-3">
        {CATEGORIES.map((cat) => {
          const items = grouped(cat.id);
          return (
            <section
              key={cat.id}
              className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden"
            >
              <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-100 dark:border-gray-800">
                <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${cat.dot}`} />
                  {cat.label}
                </h4>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-500 tabular-nums">
                  {items.length}
                </span>
              </div>
              <div className="p-3">
                {items.length === 0 ? (
                  <p className="px-1 py-2 text-xs text-gray-500 dark:text-gray-500 leading-relaxed">
                    {cat.empty}
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {items.map((req) => (
                      <div
                        key={req.id}
                        className="group flex items-start gap-2.5 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 px-3 py-2 hover:border-primary-300 dark:hover:border-primary-700 transition-colors"
                      >
                        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${cat.dot}`} />
                        <p className="flex-1 text-sm text-gray-700 dark:text-gray-300 leading-relaxed min-w-0">{req.text}</p>
                        <button
                          onClick={() => handleRemove(req.id)}
                          className="p-1 text-gray-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                          title="Remove requirement"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default RequirementsPanel;
